import { Box, Button, Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import { useShallow } from "zustand/react/shallow";
import { useImportConflicts, useResolveConflict } from "../../App/Queries/ImportQueries";
import { useNotification } from "../../App/State/Hooks/notificationHooks";
import { useImportTabStore } from "../../App/State/Stores/ImportTabStore";

export function ImportsModalFooter({ statusId }: { statusId: string }) {
  const tabValue = useImportTabStore((state) => state.tabValue);
  switch (tabValue) {
    case 0:
      return <ImportsFooter />;
    case 1:
      return <ConflictsFooter statusId={statusId} />;
    default:
      return <ErrorsFooter />; // tabValue === 2
  }
}

function ImportsFooter() {
  return (
    <Box sx={{ width: "100%", p: "0 0 0 1rem" }} data-test="imports-footer">
      <Typography component="div" variant="body1" sx={{ mb: 0.5 }}>
        This table lists candidates from a recent import. If any conflicts were detected during the process,
        they will appear in the Conflict section for you to resolve.
      </Typography>
      <Typography component="div" variant="body1">
        Resolved conflicts will appear here as a CSV Update.
      </Typography>
    </Box>
  );
}

function ErrorsFooter() {
  return (
    <Box sx={{ width: "100%", p: "0 0 0 1rem" }} data-test="errors-footer">
      <Typography component="div" variant="body1" sx={{ mb: 0.5 }}>
        An error occurs when the system detects an issue with the imported file, such as missing required
        fields.
      </Typography>
      <Typography component="div" variant="body1">
        Detailed error messages will guide you on how to correct and successfully re-import the data.
      </Typography>
    </Box>
  );
}

function ConflictsFooter({ statusId }: { statusId: string }) {
  const {
    includeResolved,
    selectedConflicts,
    setDisplayPendingOnly,
    setSelectedConflicts,
    setSelectAll,
    toQueryString,
  } = useImportTabStore(
    useShallow((state) => ({
      includeResolved: state.includeResolved,
      selectedConflicts: state.selectedConflicts,
      setDisplayPendingOnly: state.setDisplayPendingOnly,
      setSelectedConflicts: state.setSelectedConflicts,
      setSelectAll: state.setSelectAll,
      toQueryString: state.toQueryString,
    })),
  );

  const { data: conflicts } = useImportConflicts(statusId, toQueryString());
  const { mutateAsync: resolveConflicts, isLoading } = useResolveConflict();
  const { displaySuccess } = useNotification();

  const disableApproveRejectAll = isLoading || !conflicts || conflicts.totalPending === 0;
  const selectedCount = selectedConflicts.length;
  const rejectSelectedText = selectedCount > 0 ? `Reject Selected (${selectedCount})` : "Reject Selected";
  const approveSelectedText = selectedCount > 0 ? `Approve Selected (${selectedCount})` : "Approve Selected";

  const handleToggleDisplayPending = () => {
    setDisplayPendingOnly(!includeResolved);
  };

  const handleRejectAll = async () => {
    if (!disableApproveRejectAll) {
      await resolveConflicts(
        { importStatusId: statusId, approveAll: false, rejectAll: true, approveIds: [], rejectIds: [] },
        {
          onSuccess: () => {
            displaySuccess("All conflicts rejected successfully");
          },
        },
      );
    }
  };

  const handleApproveAll = async () => {
    if (!disableApproveRejectAll) {
      await resolveConflicts(
        { importStatusId: statusId, approveAll: true, rejectAll: false, approveIds: [], rejectIds: [] },
        {
          onSuccess: () => {
            displaySuccess("All conflicts approved successfully");
          },
        },
      );
    }
  };

  const handleApproveSelected = async () => {
    if (!disableApproveRejectAll && selectedCount > 0) {
      await resolveConflicts(
        {
          importStatusId: statusId,
          approveAll: false,
          rejectAll: false,
          approveIds: selectedConflicts,
          rejectIds: [],
        },
        {
          onSuccess: () => {
            displaySuccess("Selected conflicts approved successfully");
            setSelectedConflicts([]);
            setSelectAll(false);
          },
        },
      );
    }
  };

  const handleRejectSelected = async () => {
    if (!disableApproveRejectAll && selectedCount > 0) {
      await resolveConflicts(
        {
          importStatusId: statusId,
          approveAll: false,
          rejectAll: false,
          approveIds: [],
          rejectIds: selectedConflicts,
        },
        {
          onSuccess: () => {
            displaySuccess("Selected conflicts rejected successfully");
            setSelectedConflicts([]);
            setSelectAll(false);
          },
        },
      );
    }
  };

  return (
    <>
      <Box sx={{ width: "100%", p: "0 0 0 1rem", mb: 1 }} data-test="conflicts-footer">
        <Typography component="div" variant="body1" sx={{ mb: 0.5 }}>
          A conflict occurs when an imported candidate's email or phone number matches an existing record.
        </Typography>
        <Typography component="div" variant="body1">
          You can choose to either 'Approve' to update the existing candidate with new information or 'Reject'
          to keep the current record unchanged.
        </Typography>
      </Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        justifyContent={{ xs: "flex-start", md: "space-between" }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={!includeResolved}
              onChange={handleToggleDisplayPending}
              data-test="display-pending"
            />
          }
          label="Only display pending conflicts"
          sx={{ mr: { xs: 0, md: "auto" } }}
        />
        <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              data-test="reject-selected"
              variant="outlined"
              color="error"
              disabled={selectedCount === 0 || disableApproveRejectAll}
              onClick={handleRejectSelected}
            >
              {rejectSelectedText}
            </Button>
            <Button
              data-test="reject-all"
              variant="text"
              color="error"
              disabled={disableApproveRejectAll}
              onClick={handleRejectAll}
            >
              Reject All
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              data-test="approve-selected"
              variant="outlined"
              color="primary"
              disabled={selectedCount === 0 || disableApproveRejectAll}
              onClick={handleApproveSelected}
            >
              {approveSelectedText}
            </Button>
            <Button
              data-test="approve-all"
              variant="text"
              color="primary"
              disabled={disableApproveRejectAll}
              onClick={handleApproveAll}
            >
              Approve All
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </>
  );
}
